import {
  accessCandidateListResponseSchema,
  grantEditionAccessResponseSchema,
  type AccessCandidateListResponse,
  type GrantEditionAccessRequest,
  type GrantEditionAccessResponse,
  crewListResponseSchema,
  crewMemberDtoSchema,
  shiftDtoSchema,
  type LinkContextAllocationRequest,
  shiftListResponseSchema,
  staffingRoleListResponseSchema,
  staffingRoleDeletionSchema,
  shiftDeletionSchema,
  type ShiftDeletion,
  type StaffingRoleDeletion,
  type AddCrewMemberRequest,
  type CreateShiftRequest,
  type CrewListResponse,
  type CrewMemberDto,
  type SetShiftAssigneesRequest,
  type SetShiftAssignmentStatusRequest,
  type ShiftDto,
  type ShiftListResponse,
  type StaffingRoleListResponse,
  type UpdateCrewMemberRequest,
  type UpdateShiftRequest,
  myShiftListResponseSchema,
  type CreateStaffingRoleRequest,
  type MyShiftListResponse,
  type RespondToShiftRequest,
  type SetStaffingRoleHandOutsRequest,
  type UpdateStaffingRoleRequest,
} from '@tvitvi/shared';
import { queryOptions, useMutation, type UseMutationResult } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

const CREW = '/crew';
const SHIFTS = '/shifts';
const ROLES = '/staffing-roles';

// ------------------------------------------------------------------ poster og utdeling (0357)

/** Skrivingene på en post. Svaret er hele lista for året, som en vaktplan leser. */
export function useCreateStaffingRole(
  orgId: string,
): UseMutationResult<StaffingRoleListResponse, unknown, CreateStaffingRoleRequest> {
  return useMutation({
    mutationFn: (body: CreateStaffingRoleRequest) =>
      apiRequest({ path: ROLES, method: 'POST', body, schema: staffingRoleListResponseSchema }),
    meta: { invalidates: [qk.shifts(orgId)] },
  });
}

export interface UpdateStaffingRoleInput {
  id: string;
  body: UpdateStaffingRoleRequest;
}

export function useUpdateStaffingRole(
  orgId: string,
): UseMutationResult<StaffingRoleListResponse, unknown, UpdateStaffingRoleInput> {
  return useMutation({
    mutationFn: ({ id, body }: UpdateStaffingRoleInput) =>
      apiRequest({
        path: `${ROLES}/${id}`,
        method: 'PATCH',
        body,
        schema: staffingRoleListResponseSchema,
      }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId), qk.economy(orgId)] },
  });
}

/** `withShifts`: vaktene på posten går med. Svaret er kvitteringen «Angre» sender tilbake. */
export function useRemoveStaffingRole(
  orgId: string,
): UseMutationResult<StaffingRoleDeletion, unknown, { id: string; withShifts: boolean }> {
  return useMutation({
    mutationFn: ({ id, withShifts }) =>
      apiRequest({
        path: `${ROLES}/${id}`,
        method: 'DELETE',
        ...(withShifts ? { query: { withShifts: 'true' } } : {}),
        schema: staffingRoleDeletionSchema,
      }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId), qk.economy(orgId)] },
  });
}

export function useRestoreStaffingRole(
  orgId: string,
): UseMutationResult<StaffingRoleListResponse, unknown, { id: string; shifts: ShiftDeletion[] }> {
  return useMutation({
    mutationFn: ({ id, shifts }) =>
      apiRequest({
        path: `${ROLES}/${id}/restore`,
        method: 'POST',
        body: { shifts },
        schema: staffingRoleListResponseSchema,
      }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId), qk.economy(orgId)] },
  });
}

export interface SetHandOutsInput {
  id: string;
  body: SetStaffingRoleHandOutsRequest;
}

/** «Deles ut på vakta». Skriver om vaktene på posten, og derfor ressursene også. */
export function useSetStaffingRoleHandOuts(
  orgId: string,
): UseMutationResult<StaffingRoleListResponse, unknown, SetHandOutsInput> {
  return useMutation({
    mutationFn: ({ id, body }: SetHandOutsInput) =>
      apiRequest({
        path: `${ROLES}/${id}/hand-outs`,
        method: 'PUT',
        body,
        schema: staffingRoleListResponseSchema,
      }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId), qk.economy(orgId)] },
  });
}

// --------------------------------------------------------------------- «Mine vakter» (201.14)

export function myShiftsQueryOptions(orgId: string, editionId: string | null) {
  return queryOptions({
    queryKey: qk.myShifts(orgId, editionId),
    queryFn: ({ signal }): Promise<MyShiftListResponse> =>
      apiRequest({
        path: `${SHIFTS}/mine`,
        query: editionId === null ? {} : { editionId },
        schema: myShiftListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export interface RespondInput {
  shiftId: string;
  body: RespondToShiftRequest;
}

export function useRespondToShift(orgId: string): UseMutationResult<ShiftDto, unknown, RespondInput> {
  return useMutation({
    mutationFn: ({ shiftId, body }: RespondInput) =>
      apiRequest({
        path: `${SHIFTS}/${shiftId}/response`,
        method: 'PUT',
        body,
        schema: shiftDtoSchema,
      }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId), qk.economy(orgId)] },
  });
}
const PAGE_SIZE = 100;

export function crewQueryOptions(orgId: string, editionId: string, departmentId?: string) {
  return queryOptions({
    queryKey: qk.crewList(orgId, editionId, departmentId ?? null),
    queryFn: ({ signal }): Promise<CrewListResponse> =>
      apiRequest({
        path: CREW,
        query: {
          editionId,
          limit: PAGE_SIZE,
          sort: 'name',
          direction: 'asc',
          ...(departmentId === undefined ? {} : { departmentId }),
        },
        schema: crewListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * The window the plan is being read through, as ISO instants. Absent means «whatever fits a page».
 *
 * A festival's plan is 360 shifts and `MAX_PAGE_SIZE` is 100, so the grid asks for the days it is
 * drawing rather than for the first hundred rows — see the contract's own header. The window is
 * part of the query key, because two windows are two answers and not two pages of one.
 */
export interface ShiftWindow {
  from: string;
  to: string;
}

export function shiftsQueryOptions(
  orgId: string,
  editionId: string,
  understaffed: boolean,
  window?: ShiftWindow,
) {
  return queryOptions({
    queryKey: qk.shiftList(orgId, editionId, understaffed, window?.from, window?.to),
    queryFn: ({ signal }): Promise<ShiftListResponse> =>
      apiRequest({
        path: SHIFTS,
        query: {
          editionId,
          limit: PAGE_SIZE,
          sort: 'start',
          direction: 'asc',
          ...(understaffed ? { understaffed: 'true' } : {}),
          ...(window === undefined ? {} : { from: window.from, to: window.to }),
        },
        schema: shiftListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * VAKTENE UTEN POST, HELE ÅRGANGEN (bølge 0, A15). Sjåførvakta på en bil uten post står løs, og
 * rutenettet viser bare uka på skjermen: den fantes og kunne ikke ses. Første side holder — en
 * årgang med over hundre løse vakter har et annet problem enn denne lista.
 */
export function looseShiftsQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.looseShifts(orgId, editionId),
    queryFn: ({ signal }): Promise<ShiftListResponse> =>
      apiRequest({
        path: SHIFTS,
        query: {
          editionId,
          limit: PAGE_SIZE,
          sort: 'start',
          direction: 'asc',
          withoutRole: 'true',
        },
        schema: shiftListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function useAddCrewMember(
  orgId: string,
): UseMutationResult<CrewMemberDto, unknown, AddCrewMemberRequest> {
  return useMutation({
    mutationFn: (body: AddCrewMemberRequest) =>
      apiRequest({ path: CREW, method: 'POST', body, schema: crewMemberDtoSchema }),
    meta: { invalidates: [qk.crew(orgId)] },
  });
}

export interface UpdateCrewMemberInput {
  crewId: string;
  body: UpdateCrewMemberRequest;
}

export function useUpdateCrewMember(
  orgId: string,
): UseMutationResult<CrewMemberDto, unknown, UpdateCrewMemberInput> {
  return useMutation({
    mutationFn: ({ crewId, body }: UpdateCrewMemberInput) =>
      apiRequest({ path: `${CREW}/${crewId}`, method: 'PATCH', body, schema: crewMemberDtoSchema }),
    meta: { invalidates: [qk.crew(orgId)] },
  });
}

export function useRemoveCrewMember(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (crewId: string) => apiRequest({ path: `${CREW}/${crewId}`, method: 'DELETE' }),
    meta: { invalidates: [qk.crew(orgId)] },
  });
}

export function useCreateShift(
  orgId: string,
): UseMutationResult<ShiftDto, unknown, CreateShiftRequest> {
  return useMutation({
    mutationFn: (body: CreateShiftRequest) =>
      apiRequest({ path: SHIFTS, method: 'POST', body, schema: shiftDtoSchema }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId), qk.economy(orgId)] },
  });
}

export interface UpdateShiftInput {
  shiftId: string;
  body: UpdateShiftRequest;
}

/**
 * VAKTENE STÅR OGSÅ PÅ KONSERTEN OG KVELDEN (bølge 2, 29.09): konsertens «Personer» og kveldens
 * «Vakter» leser de samme skiftene under `qk.orgConcerts`/`qk.venueHouse`, og hurtigredigering der
 * går gjennom disse krokene. Uten prefiksene sto den gamle verdien i cella til neste henting.
 */
function shiftSweep(orgId: string) {
  return [
    qk.shifts(orgId),
    qk.resources(orgId),
    qk.economy(orgId),
    qk.orgConcerts(orgId),
    qk.venueHouse(orgId),
  ];
}

/** «Tiden skal kunne endres direkte» — the same PATCH the post and the need go through. */
export function useUpdateShift(
  orgId: string,
): UseMutationResult<ShiftDto, unknown, UpdateShiftInput> {
  return useMutation({
    mutationFn: ({ shiftId, body }: UpdateShiftInput) =>
      apiRequest({ path: `${SHIFTS}/${shiftId}`, method: 'PATCH', body, schema: shiftDtoSchema }),
    meta: { invalidates: shiftSweep(orgId) },
  });
}

export interface SetShiftAssigneesInput {
  shiftId: string;
  body: SetShiftAssigneesRequest;
}

/** Staffing a shift invalidates the shift list only: the crew list did not change. */
export function useSetShiftAssignees(
  orgId: string,
): UseMutationResult<ShiftDto, unknown, SetShiftAssigneesInput> {
  return useMutation({
    mutationFn: ({ shiftId, body }: SetShiftAssigneesInput) =>
      apiRequest({
        path: `${SHIFTS}/${shiftId}/assignees`,
        method: 'PUT',
        body,
        schema: shiftDtoSchema,
      }),
    meta: { invalidates: shiftSweep(orgId) },
  });
}

export interface LinkShiftAllocationInput {
  shiftId: string;
  body: LinkContextAllocationRequest;
}

/**
 * «VELG …» PÅ VAKTA (trinn 5.3) — bestillingen og knytningen i ett kall.
 *
 * FEIER BEGGE PREFIKSENE. Skiftet endrer seg, og det gjør Ressurser-siden også: den samme bilen
 * står i lista, i dagsrutenettet, i konfliktstripa og i tellerne. Det er den ene linja som er lett
 * å glemme, og reisefanen sier det samme om seg selv.
 */
export function useLinkShiftAllocation(
  orgId: string,
): UseMutationResult<ShiftDto, unknown, LinkShiftAllocationInput> {
  return useMutation({
    mutationFn: ({ shiftId, body }: LinkShiftAllocationInput) =>
      apiRequest({
        path: `${SHIFTS}/${shiftId}/allocations`,
        method: 'POST',
        body,
        schema: shiftDtoSchema,
      }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId)] },
  });
}

export interface UnlinkShiftAllocationInput {
  shiftId: string;
  allocationId: string;
}

/** «Løsne» — vakta slipper ressursen; ressursen består. Navngitt, fordi en vakt kan ha flere. */
export function useUnlinkShiftAllocation(
  orgId: string,
): UseMutationResult<ShiftDto, unknown, UnlinkShiftAllocationInput> {
  return useMutation({
    mutationFn: ({ shiftId, allocationId }: UnlinkShiftAllocationInput) =>
      apiRequest({
        path: `${SHIFTS}/${shiftId}/allocations/${allocationId}`,
        method: 'DELETE',
        schema: shiftDtoSchema,
      }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId)] },
  });
}

export function useRemoveShift(orgId: string): UseMutationResult<ShiftDeletion, unknown, string> {
  return useMutation({
    mutationFn: (shiftId: string) =>
      apiRequest({ path: `${SHIFTS}/${shiftId}`, method: 'DELETE', schema: shiftDeletionSchema }),
    /* `shiftSweep`: husets vaktplan (`qk.venueHouse`) leser de samme vaktene (QA6-K4 M4). */
    meta: { invalidates: shiftSweep(orgId) },
  });
}

/** «Angre» på en slettet vakt: kvitteringen fra slettingen, tilbake (23. september). */
export function useRestoreShift(orgId: string): UseMutationResult<ShiftDto, unknown, ShiftDeletion> {
  return useMutation({
    mutationFn: (receipt: ShiftDeletion) =>
      apiRequest({
        path: `${SHIFTS}/${receipt.id}/restore`,
        method: 'POST',
        body: { allocationIds: receipt.allocationIds },
        schema: shiftDtoSchema,
      }),
    meta: { invalidates: shiftSweep(orgId) },
  });
}

/**
 * Poster (PEOPLE.md, fane 1) — the timeline’s own rows.
 *
 * `CACHE.reference`, like departments and tags: a post is Settings data that changes when somebody
 * reorganises the festival, not while a plan is being read.
 */
export function staffingRolesQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.staffingRoles(orgId, editionId),
    queryFn: ({ signal }): Promise<StaffingRoleListResponse> =>
      apiRequest({
        path: ROLES,
        query: { editionId },
        schema: staffingRoleListResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

export interface ShiftAnswer {
  shiftId: string;
  body: SetShiftAssignmentStatusRequest;
}

/** «Bekreftelsesstatus» — one person answering. Sweeps the shift list, which counts the yeses. */
export function useSetShiftAssignmentStatus(
  orgId: string,
): UseMutationResult<ShiftDto, unknown, ShiftAnswer> {
  return useMutation({
    mutationFn: ({ shiftId, body }: ShiftAnswer) =>
      apiRequest({
        path: `${SHIFTS}/${shiftId}/assignment-status`,
        method: 'PUT',
        body,
        schema: shiftDtoSchema,
      }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId), qk.economy(orgId)] },
  });
}

/* ==============================================================================================
 * SAK 182 — «LEGG TIL ANSATTE» PÅ ÅRGANGENS PERSONER.
 *
 * Atle: «knapp «Legg til ansatte» som lister medlemmer uten tilgang til årgangen … Den som legges
 * til her får både tilgang og står på bemanningen.»
 * ============================================================================================== */

export function editionAccessCandidatesQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.editionAccessCandidates(orgId, editionId),
    queryFn: ({ signal }): Promise<AccessCandidateListResponse> =>
      apiRequest({
        path: `/editions/${editionId}/access/candidates`,
        schema: accessCandidateListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export interface GrantEditionAccessInput {
  editionId: string;
  body: GrantEditionAccessRequest;
}

/**
 * ÉN INVALIDERING FOR BEGGE LISTENE, og det er ikke en forkortelse.
 *
 * Kallet skriver to steder — tilgangen og bemanningen — så et svar som bare friskner opp det ene
 * ville latt den andre lista vise gårsdagens sannhet til noen trykket oppdater. `qk.crew(orgId)`
 * er prefikset begge ligger under, nettopp for at dette ikke skal være to linjer å huske.
 */
export function useGrantEditionAccess(
  orgId: string,
): UseMutationResult<GrantEditionAccessResponse, unknown, GrantEditionAccessInput> {
  return useMutation({
    mutationFn: ({ editionId, body }: GrantEditionAccessInput) =>
      apiRequest({
        path: `/editions/${editionId}/access`,
        method: 'POST',
        body,
        schema: grantEditionAccessResponseSchema,
      }),
    meta: { invalidates: [qk.crew(orgId)] },
  });
}

export interface RevokeEditionAccessInput {
  editionId: string;
  personId: string;
}

export function useRevokeEditionAccess(
  orgId: string,
): UseMutationResult<void, unknown, RevokeEditionAccessInput> {
  return useMutation({
    mutationFn: ({ editionId, personId }: RevokeEditionAccessInput) =>
      apiRequest({ path: `/editions/${editionId}/access/${personId}`, method: 'DELETE' }),
    meta: { invalidates: [qk.crew(orgId)] },
  });
}
